'use client';

import React, { useContext, useState, useRef, useEffect } from 'react';
import validator from 'validator';

import Icon from '@mdi/react';
import { mdiEmail } from '@mdi/js';

import { ThemeContext } from '@dstantwo/ullr-react-components';

import iconSet from '../../../src/assets/fonts/ullrFeatureIcons/selection.json';
import LoginInput from '../../../src/components/LoginInput/LoginInput';
import IcomoonReact from 'icomoon-react';

import {
  PageContainer,
  ColumnContainer,
  InnerCardContainer,
  LoginHeader,
  LogoContainer,
  UllrLogo,
  ErrorText,
  InstructionText,
} from '../../../src/login/styledComponents';
import { UllrLoginCard } from '../../../src/components/ui/UllrCard';
import { UllrButton } from '../../../src/components/ui/UllrButton';

import { ullrApi, ullrRecaptcha } from '../../../src/services';

import background from '../../../src/assets/images/contours.svg';
import '../../../src/App.css';

export default function DemoLiftsPage() {
  const { COLORS } = useContext(ThemeContext);

  // state
  const [name, setName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [resortName, setResortName] = useState('');
  const [email, setEmail] = useState('');
  const [isRequestInFlight, setIsRequestInFlight] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  // refs
  const nameRef = useRef<any>(null);
  const jobTitleRef = useRef<any>(null);
  const resortNameRef = useRef<any>(null);
  const emailRef = useRef<any>(null);

  // effects
  useEffect(() => {
    ullrRecaptcha.load();
    return ullrRecaptcha.remove;
  }, []);

  const submitUserInfo = async () => {
    setIsRequestInFlight(true);
    // validate input values
    let allInputsValid = true;
    allInputsValid = (await nameRef.current.validate(name)) && allInputsValid;
    allInputsValid = (await jobTitleRef.current.validate(jobTitle)) && allInputsValid;
    allInputsValid = (await resortNameRef.current.validate(resortName)) && allInputsValid;
    allInputsValid = (await emailRef.current.validate(email)) && allInputsValid;
    if (!allInputsValid) {
      setIsRequestInFlight(false);
      return;
    }

    // submit user info with recaptcha
    ullrRecaptcha.ready(() => {
      ullrRecaptcha.execute({ action: 'web_publicDemo' }).then((captchaToken: string) => {
        ullrApi.feedback
          .postFeedback({
            captchaToken,
            subject: 'Public Demo View (Lifts) - Web',
            comments: JSON.stringify(
              {
                name,
                jobTitle,
                resortName,
                email,
              },
              null,
              2
            ),
          })
          .then((response: any) => {
            if (response && response.status === 200) {
              setShowDemo(true);
            } else {
              throw new Error();
            }
          })
          .catch(() => {
            setErrorText('Error. Please make sure that you have all fields filled out and try again.');
          })
          .finally(() => {
            setIsRequestInFlight(false);
          });
      });
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      submitUserInfo();
    }
  };

  return (
    <PageContainer
      style={{
        backgroundImage: `url(${(background as any).src || background})`,
      }}
    >
      <ColumnContainer>
        <LogoContainer>
          <IcomoonReact iconSet={iconSet} color={COLORS.DARK} size={50} icon="ullr-jump" />
          <UllrLogo>Ullr</UllrLogo>
        </LogoContainer>
        <UllrLoginCard
          style={{
            alignSelf: 'stretch',
          }}
        >
          {!showDemo && (
            <InnerCardContainer>
              <LoginHeader>Ullr Lifts Demo (5 mins)</LoginHeader>
              <InstructionText>Please share some information about yourself to access the demo.</InstructionText>
              <LoginInput
                ref={nameRef}
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                type="text"
                name="name"
                placeholder="Your name"
                required="required"
                onKeyPress={handleKeyPress}
              />
              <LoginInput
                ref={resortNameRef}
                value={resortName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResortName(e.target.value)}
                type="text"
                name="resort name"
                placeholder="Ski resort name"
                required="required"
                onKeyPress={handleKeyPress}
              />
              <LoginInput
                ref={jobTitleRef}
                value={jobTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJobTitle(e.target.value)}
                type="text"
                name="job title"
                placeholder="Job title"
                required="required"
                onKeyPress={handleKeyPress}
              />
              <LoginInput
                ref={emailRef}
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                type="text"
                name="email"
                placeholder="Email address"
                required="required"
                onKeyPress={handleKeyPress}
                validate={async (value: string) => {
                  if (!validator.isEmail(value)) {
                    throw new Error('Please enter a valid email address');
                  }
                }}
              />
              {!!errorText && (
                <div>
                  <ErrorText>{errorText}</ErrorText>
                </div>
              )}
              <UllrButton onClick={() => submitUserInfo()} isLoading={isRequestInFlight}>
                Access Demo
              </UllrButton>
            </InnerCardContainer>
          )}
          {showDemo && (
            <InnerCardContainer>
              <LoginHeader>Ullr Lifts Demo (5 mins)</LoginHeader>
              <InstructionText>If you have any questions or would like to see more, please reach out to david@ullr.ski</InstructionText>

              {/* Loom */}
              <div
                style={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                  alignSelf: 'stretch',
                }}
              >
                <iframe
                  title="ullr-demo"
                  src="https://www.loom.com/embed/5c90d8e5160b4032bfc5ef30ab2c2950?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                ></iframe>
              </div>

              <UllrButton
                style={{
                  marginTop: 36,
                }}
                onClick={() => {
                  window.open('https://ullr.ski/contact/');
                }}
              >
                <Icon path={mdiEmail} size={0.8} color={COLORS.WHITE} style={{ marginRight: 4 }} />
                {`Contact us for more info!`}
              </UllrButton>
            </InnerCardContainer>
          )}
        </UllrLoginCard>
      </ColumnContainer>
    </PageContainer>
  );
}
